import { alpha, type Theme } from '@mui/material';
import { diffDaysFromAppToday } from '../../../config/timezone';
import type { LotStatus } from '../../../services/lots/lots.types';
import { MODULE_ACCENT_COLORS } from '../../../theme/module-colors';

const ACCENT = MODULE_ACCENT_COLORS.lots.main;

export type LotVisualState =
  | 'active'
  | 'expiring-soon'
  | 'expiring-today'
  | 'expired'
  | 'exhausted'
  | 'blocked';

interface LotVisualInput {
  status: LotStatus;
  expiryDate: string | null | undefined;
  /**
   * Pass a fixed reference date for deterministic tests; defaults to now.
   * The instant is interpreted in Europe/Rome to derive the comparison day.
   */
  now?: Date;
}

export function resolveLotVisualState({ status, expiryDate, now }: LotVisualInput): LotVisualState {
  // BLOCKED (ritiro/richiamo in corso) wins over any expiry-based derivation:
  // a lot segregated for a recall must never read as merely "expired".
  if (status === 'BLOCKED') {
    return 'blocked';
  }
  if (status === 'EXHAUSTED') {
    return 'exhausted';
  }
  if (status === 'EXPIRED') {
    return 'expired';
  }
  const diffDays = diffDaysFromAppToday(expiryDate, now);
  if (diffDays === null) {
    return 'active';
  }
  if (diffDays < 0) {
    return 'expired';
  }
  if (diffDays === 0) {
    return 'expiring-today';
  }
  if (diffDays <= 3) {
    return 'expiring-soon';
  }
  return 'active';
}

/**
 * A label is printable only while the lot is usable: expired and exhausted lots
 * hide the print action (a label for a dead lot is misleading on the shelf).
 */
export function isLotLabelPrintable(state: LotVisualState): boolean {
  return state !== 'expired' && state !== 'exhausted' && state !== 'blocked';
}

/**
 * Same rule as printing: a dead lot accepts no new QR labels, since a sticker
 * pointing at an exhausted or expired lot is a traceability lie on the shelf.
 * The backend enforces it too (400 on bind).
 */
export function isLotQrBindable(state: LotVisualState): boolean {
  return isLotLabelPrintable(state);
}

/**
 * Single source of color truth for a lot card. Every visual surface (border,
 * background tint, icon block, status badge, date pill) reads from this map so
 * the card communicates one coherent state.
 *
 * All states stay within the project's soft-chip language: light tinted
 * surfaces, colored text/borders. Never solid filled.
 */
export interface LotStateColors {
  main: string;
  /** Darker shade for text on tinted backgrounds (better contrast). */
  dark: string;
  /** Subtle card background tint. Use 'transparent' to fall back to paper. */
  surface: string;
  border: string;
}

export function getLotStateColors(theme: Theme, state: LotVisualState): LotStateColors {
  switch (state) {
    case 'expired':
    case 'blocked':
      return {
        main: theme.palette.error.main,
        dark: theme.palette.error.dark,
        surface: alpha(theme.palette.error.main, 0.035),
        border: alpha(theme.palette.error.main, 0.35),
      };
    case 'expiring-today':
      return {
        main: theme.palette.warning.main,
        dark: theme.palette.warning.dark,
        surface: alpha(theme.palette.warning.main, 0.04),
        border: alpha(theme.palette.warning.main, 0.4),
      };
    case 'expiring-soon':
      return {
        main: theme.palette.warning.main,
        dark: theme.palette.warning.dark,
        surface: 'transparent',
        border: alpha(theme.palette.warning.main, 0.3),
      };
    case 'exhausted':
      return {
        main: theme.palette.text.secondary,
        dark: theme.palette.text.primary,
        surface: alpha(theme.palette.text.primary, 0.02),
        border: theme.palette.divider,
      };
    default:
      return {
        main: ACCENT,
        dark: MODULE_ACCENT_COLORS.lots.dark,
        surface: 'transparent',
        border: theme.palette.divider,
      };
  }
}
